import { getMyLocations } from '@jq4race/api-client';
import { LanguageSwitcher, Trans, useTranslation } from '@jq4race/i18n';
import type { AssignedLocationResponse } from '@jq4race/shared';
import { LocationPicker } from '@jq4race/ui';
import logoSecondary from '@jq4race/ui/assets/logos/logo-secondary.png';
import patternDecoFan from '@jq4race/ui/assets/patterns/pattern-cream-deco-fan.png';
import { Link, useLocation, useMatches } from '@tanstack/react-router';
import {
  Bell,
  ChevronRight,
  LogOut,
  MapPin,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { enumLabel } from '../../../shared/i18n/enum-label';
import { useAuthState } from '../../auth/ui/auth-state-context';
import { SubscriptionStatusBanner } from '../../subscription/ui/components/SubscriptionStatusBanner';
import { NAV_GROUPS, type NavGroup, type NavItem, type RouteMeta } from '../config/navigation';
import {
  getActiveLocationId,
  setActiveLocationId,
  useActiveLocationId,
} from '../lib/active-location-store';
import { translateBreadcrumbSegment } from '../lib/breadcrumb';
import { switchActiveLocation } from '../lib/switch-active-location';
import { LocationSwitcher } from './LocationSwitcher';

const COLLAPSE_KEY = 'jq-admin-sidebar-collapsed';

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation('app-shell');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  });
  const location = useLocation();
  const matches = useMatches();

  // ── Supervisor location picker ──────────────────────────────────────────
  const { session } = useAuthState();
  const role = session?.user.role ?? '';
  const [pickerLocations, setPickerLocations] = useState<AssignedLocationResponse[]>([]);
  const activeLocationId = useActiveLocationId();
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    if (role !== 'supervisor') return;

    async function loadLocations() {
      try {
        const res = await getMyLocations();
        const activeOnes = res.locations.filter((l) => l.status === 'active');
        setPickerLocations(activeOnes);

        if (activeOnes.length === 1 && activeOnes[0]) {
          // Auto-select single location.
          const id = activeOnes[0].id;
          setActiveLocationId(id); // persists + notifies subscribers (dashboard page)
          return;
        }

        if (activeOnes.length >= 2) {
          const cached = getActiveLocationId();
          const isValid = cached && activeOnes.some((l) => l.id === cached);
          if (!isValid) {
            setActiveLocationId(null);
            setShowPicker(true);
          }
        }
      } catch {
        // Non-blocking — failure means we can't load locations.
      }
    }

    void loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const pathname = location.pathname;
  // biome-ignore lint/correctness/useExhaustiveDependencies: close drawer on route transitions
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  const breadcrumb = useMemo<string[]>(() => {
    for (let i = matches.length - 1; i >= 0; i -= 1) {
      const meta = matches[i]?.staticData as RouteMeta | undefined;
      if (meta?.breadcrumb && meta.breadcrumb.length > 0) {
        return meta.breadcrumb.map((segment) => translateBreadcrumbSegment(t, segment));
      }
    }
    return [t('shell.managerConsole')];
  }, [matches, t]);

  const toggleCollapsed = () => setCollapsed((v) => !v);

  const handleLocationChange = (id: string) => {
    switchActiveLocation(id, activeLocationId);
  };

  const visibleGroups = useMemo(() => {
    const role = session?.user.role;
    const allowed = (roles?: NavGroup['requiredRoles']) => !roles || roles.some((r) => r === role);
    return NAV_GROUPS.filter((g) => allowed(g.requiredRoles))
      .map((g) => ({ ...g, items: g.items.filter((i) => allowed(i.requiredRoles)) }))
      .filter((g) => g.items.length > 0);
  }, [session?.user.role]);

  return (
    <div className="min-h-screen bg-jq-paper text-foreground">
      {/* ----- Desktop sidebar ----- */}
      <aside
        className={`fixed bottom-4 left-0 top-4 z-30 hidden flex-col overflow-hidden rounded-r-lg border-y border-r border-jq-dark-2 bg-jq-ink text-jq-paper shadow-lg transition-[width] duration-200 ease-jq lg:flex ${
          collapsed ? 'w-20' : 'w-64 xl:w-72'
        }`}
      >
        <SidebarContent
          groups={visibleGroups}
          currentPath={location.pathname}
          collapsed={collapsed}
        />
      </aside>

      {/* ----- Mobile drawer ----- */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${mobileOpen ? '' : 'pointer-events-none'}`}
        aria-hidden={!mobileOpen}
      >
        <button
          type="button"
          aria-label={t('topbar.closeMenu')}
          tabIndex={mobileOpen ? 0 : -1}
          onClick={() => setMobileOpen(false)}
          className={`absolute inset-0 bg-jq-ink/40 backdrop-blur-sm transition-opacity duration-200 ${
            mobileOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <aside
          className={`absolute left-0 top-0 flex h-full w-72 flex-col bg-jq-ink text-jq-paper shadow-lg transition-transform duration-300 ease-jq ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <SidebarContent
            groups={visibleGroups}
            currentPath={location.pathname}
            collapsed={false}
          />
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label={t('topbar.closeMenu')}
            className="absolute right-4 top-4 z-10 rounded p-2 text-jq-navy-200 transition hover:bg-white/5 hover:text-jq-paper"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </aside>
      </div>

      {/* ----- Main column ----- */}
      <div
        className={`flex min-h-screen flex-col transition-[padding] duration-200 ease-jq ${
          collapsed ? 'lg:pl-20' : 'lg:pl-64 xl:pl-72'
        }`}
      >
        <Topbar
          breadcrumb={breadcrumb}
          collapsed={collapsed}
          onMenuClick={() => setMobileOpen(true)}
          onToggleCollapsed={toggleCollapsed}
          role={role}
          pickerLocations={pickerLocations}
          activeLocationId={activeLocationId}
          onLocationChange={handleLocationChange}
        />

        <main className="relative flex-1 overflow-hidden">
          <img
            src={patternDecoFan}
            alt=""
            aria-hidden
            className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.05] [mask-image:radial-gradient(ellipse_at_top,black_55%,transparent_100%)]"
          />
          <div className="relative w-full px-6 py-8 sm:px-10 lg:px-12 lg:py-12">
            {/* ── Location context indicator ─────────────────────────────── */}
            {role === 'org_admin' && (
              <div className="mb-6 flex items-center gap-2 text-[12px] text-jq-graphite">
                <MapPin className="h-3.5 w-3.5 text-jq-stone" strokeWidth={1.5} />
                <span>
                  <Trans
                    t={t}
                    i18nKey="orgAdminBanner.text"
                    components={{ strong: <strong className="font-medium text-jq-charcoal" /> }}
                  />
                </span>
              </div>
            )}
            <SubscriptionStatusBanner userRole={role} />
            {children}
          </div>
        </main>

        {/* ── Supervisor location picker modal ──────────────────────────── */}
        {showPicker && role === 'supervisor' && (
          <LocationPicker
            locations={pickerLocations}
            selectedId={activeLocationId ?? undefined}
            forceSelect
            labels={{
              title: t('locationPicker.title'),
              description: t('locationPicker.description'),
              emptyDescription: t('locationPicker.emptyDescription'),
              noLocations: t('locationPicker.noLocations'),
              changeAnytime: t('locationPicker.changeAnytime'),
            }}
            onSelect={(id) => {
              setActiveLocationId(id);
              setShowPicker(false);
            }}
          />
        )}

        <footer className="border-t border-jq-sand/50 bg-jq-surface-1/60 px-6 py-4 text-center text-[11px] uppercase tracking-widest text-jq-stone lg:px-12 lg:text-left">
          {t('footer.copyright', {
            year: new Date().getFullYear(),
            tagline: t('shell.managerConsole'),
          })}
        </footer>
      </div>
    </div>
  );
}

function SidebarContent({
  groups,
  currentPath,
  collapsed,
}: {
  groups: NavGroup[];
  currentPath: string;
  collapsed: boolean;
}) {
  const { t } = useTranslation('app-shell');

  return (
    <>
      <div
        className={`relative flex h-20 items-center gap-3 border-b border-jq-dark-2 ${
          collapsed ? 'justify-center px-2' : 'px-6'
        }`}
      >
        <img src={logoSecondary} alt="JQRACE4" className="h-10 w-auto shrink-0" />
        {!collapsed && (
          <div className="flex flex-col leading-none">
            <span className="font-ceremony text-[14px] font-bold uppercase tracking-[0.18em] text-jq-paper">
              JQRACE4
            </span>
            <span className="mt-1 text-[10px] uppercase tracking-[0.22em] text-jq-gold-bright">
              {t('shell.managerConsole')}
            </span>
          </div>
        )}
      </div>

      <nav className={`flex-1 overflow-y-auto py-6 ${collapsed ? 'px-2' : 'px-3'}`}>
        {groups.map((group) => (
          <div key={group.id} className="mb-6 last:mb-0">
            {collapsed ? (
              <span aria-hidden className="mx-3 mb-2 block h-px bg-jq-dark-2 first:hidden" />
            ) : (
              <p className="px-3 pb-2 text-[10px] font-medium uppercase tracking-[0.22em] text-jq-navy-200">
                {t(`nav.groups.${group.id}`)}
              </p>
            )}
            <ul className="space-y-1">
              {group.items.map((item) => (
                <li key={item.id}>
                  <NavLinkItem item={item} currentPath={currentPath} collapsed={collapsed} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <UserPanel collapsed={collapsed} />
    </>
  );
}

function NavLinkItem({
  item,
  currentPath,
  collapsed,
}: {
  item: NavItem;
  currentPath: string;
  collapsed: boolean;
}) {
  const { t } = useTranslation('app-shell');
  const Icon = item.icon;
  const label = t(`nav.items.${item.id}`);

  if (item.disabled || !item.to) {
    const disabledNode = (
      <div
        aria-disabled
        className={`group relative flex cursor-not-allowed items-center gap-3 rounded-md py-2.5 text-[14px] text-jq-navy-200/70 ${
          collapsed ? 'justify-center px-0' : 'px-3'
        }`}
      >
        <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.5} />
        {!collapsed && (
          <>
            <span className="flex-1">{label}</span>
            <span className="rounded-full border border-jq-navy-700 px-2 py-0.5 text-[9px] font-medium uppercase tracking-widest text-jq-navy-200">
              {t('nav.comingSoonBadge')}
            </span>
          </>
        )}
      </div>
    );

    if (collapsed) {
      return (
        <SidebarTooltip label={label} hint={t('nav.comingSoonHint')}>
          {disabledNode}
        </SidebarTooltip>
      );
    }
    return disabledNode;
  }

  const isActive =
    currentPath === item.to || (item.to !== '/dashboard' && currentPath.startsWith(`${item.to}/`));

  const linkNode = (
    <Link
      to={item.to}
      className={`group relative flex items-center gap-3 rounded-md py-2.5 text-[14px] font-medium transition-colors duration-200 ease-jq ${
        collapsed ? 'justify-center px-0' : 'px-3'
      } ${
        isActive
          ? 'bg-jq-navy-700 text-jq-paper'
          : 'text-jq-navy-200 hover:bg-white/[0.04] hover:text-jq-paper'
      }`}
    >
      {isActive && (
        <span
          aria-hidden
          className="absolute inset-y-1.5 left-0 w-[3px] rounded-r-full bg-jq-gold-bright"
        />
      )}
      <Icon
        className={`h-[18px] w-[18px] shrink-0 ${
          isActive ? 'text-jq-gold-bright' : 'text-jq-navy-200 group-hover:text-jq-paper'
        }`}
        strokeWidth={1.5}
      />
      {!collapsed && <span className="flex-1">{label}</span>}
    </Link>
  );

  if (collapsed) {
    return <SidebarTooltip label={label}>{linkNode}</SidebarTooltip>;
  }
  return linkNode;
}

function SidebarTooltip({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  function show() {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({ top: rect.top + rect.height / 2, left: rect.right + 12 });
  }
  function hide() {
    setPos(null);
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: pointer observer wrapper, real interaction lives on child
    <div ref={wrapperRef} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
      {children}
      {pos &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-[60] -translate-y-1/2"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="relative rounded-md border border-jq-navy-700 bg-jq-dark-2 px-3 py-1.5 shadow-lg">
              <span
                aria-hidden
                className="absolute -left-[5px] top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 border-b border-l border-jq-navy-700 bg-jq-dark-2"
              />
              <span className="block whitespace-nowrap text-[12px] font-medium leading-tight text-jq-paper">
                {label}
              </span>
              {hint && (
                <span className="mt-1 block whitespace-nowrap text-[10px] font-medium uppercase tracking-[0.18em] text-jq-gold-bright">
                  {hint}
                </span>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

/** Exported for testing (account footer of the sidebar). */
export function UserPanel({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation('app-shell');
  const { session, signOut } = useAuthState();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (collapsed) setOpen(false);
  }, [collapsed]);

  const email = session?.user.email ?? '';
  // Raw `super_admin` is a backend enum value, not copy: translate it.
  const role = session?.user.role ? enumLabel(t, 'userRole', session.user.role) : '';
  const initials = computeInitials(email);

  async function onSignOut() {
    setOpen(false);
    await signOut();
  }

  if (collapsed) {
    return (
      <div className="relative border-t border-jq-dark-2 p-2">
        <SidebarTooltip label={email || t('shell.signOut')} hint={t('shell.signOut')}>
          <button
            type="button"
            onClick={onSignOut}
            aria-label={t('shell.signOutFor', { email })}
            className="flex w-full items-center justify-center rounded-md p-2 transition hover:bg-white/[0.04]"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-jq-navy-700 font-sans text-[12px] font-semibold uppercase tracking-wide text-jq-gold-bright ring-1 ring-jq-gold/40">
              {initials}
            </span>
          </button>
        </SidebarTooltip>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative border-t border-jq-dark-2 p-3">
      {open && (
        <div className="absolute bottom-[calc(100%+4px)] left-3 right-3 rounded-md border border-jq-navy-700 bg-jq-dark-2 p-1 shadow-lg">
          <div className="px-3 py-2 text-[12px] text-jq-navy-200">
            <p className="truncate text-jq-paper">{email}</p>
            <p className="mt-0.5 uppercase tracking-widest text-jq-gold-bright">{role}</p>
          </div>
          <div className="my-1 h-px bg-jq-navy-700" />
          <button
            type="button"
            onClick={onSignOut}
            className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-[13px] font-medium text-jq-paper transition hover:bg-white/[0.05]"
          >
            <LogOut className="h-4 w-4 text-jq-navy-200" strokeWidth={1.5} />
            {t('shell.signOut')}
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition hover:bg-white/[0.04]"
        aria-expanded={open}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-jq-navy-700 font-sans text-[12px] font-semibold uppercase tracking-wide text-jq-gold-bright ring-1 ring-jq-gold/40">
          {initials}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-medium text-jq-paper">{email}</span>
          <span className="block truncate text-[11px] uppercase tracking-widest text-jq-navy-200">
            {role}
          </span>
        </span>
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-jq-navy-200 transition-transform ${
            open ? 'rotate-90' : ''
          }`}
          strokeWidth={1.5}
        />
      </button>
    </div>
  );
}

function Topbar({
  breadcrumb,
  collapsed,
  onMenuClick,
  onToggleCollapsed,
  role,
  pickerLocations,
  activeLocationId,
  onLocationChange,
}: {
  breadcrumb: string[];
  collapsed: boolean;
  onMenuClick: () => void;
  onToggleCollapsed: () => void;
  role: string;
  pickerLocations: AssignedLocationResponse[];
  activeLocationId: string | null;
  onLocationChange: (id: string) => void;
}) {
  const { t } = useTranslation('app-shell');
  const collapseLabel = collapsed ? t('topbar.expandSidebar') : t('topbar.collapseSidebar');
  const showLocationSwitcher = role === 'supervisor' && pickerLocations.length >= 2;

  return (
    <header className="sticky top-0 z-20 border-b border-jq-sand/60 bg-jq-paper/85 backdrop-blur supports-[backdrop-filter]:bg-jq-paper/70">
      <div className="flex h-14 items-center gap-2 px-4 sm:gap-3 sm:px-8 lg:px-10">
        <button
          type="button"
          onClick={onMenuClick}
          aria-label={t('topbar.openMenu')}
          className="-ml-2 rounded p-2 text-jq-graphite transition hover:bg-jq-surface-2 hover:text-jq-navy lg:hidden"
        >
          <Menu className="h-5 w-5" strokeWidth={1.5} />
        </button>

        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapseLabel}
          aria-pressed={collapsed}
          title={collapseLabel}
          className="-ml-2 hidden rounded p-2 text-jq-graphite transition hover:bg-jq-surface-2 hover:text-jq-navy lg:inline-flex"
        >
          {collapsed ? (
            <PanelLeftOpen className="h-5 w-5" strokeWidth={1.5} />
          ) : (
            <PanelLeftClose className="h-5 w-5" strokeWidth={1.5} />
          )}
        </button>

        <Breadcrumb segments={breadcrumb} />

        <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-2">
          {showLocationSwitcher && (
            <>
              <LocationSwitcher
                locations={pickerLocations}
                activeLocationId={activeLocationId}
                onLocationChange={onLocationChange}
              />
              <span aria-hidden className="mx-1 hidden h-6 w-px bg-jq-sand/70 sm:block" />
            </>
          )}
          <SearchPill />
          <NotificationsButton hasUnread />
          <span aria-hidden className="mx-1 hidden h-6 w-px bg-jq-sand/70 sm:block" />
          <LanguageSwitcher />
          <span aria-hidden className="mx-1 hidden h-6 w-px bg-jq-sand/70 sm:block" />
          <HeaderUserMenu />
        </div>
      </div>
    </header>
  );
}

function Breadcrumb({ segments }: { segments: string[] }) {
  const { t } = useTranslation('app-shell');
  if (segments.length === 0) return null;
  const last = segments[segments.length - 1] ?? '';
  const parents = segments.slice(0, -1);

  return (
    <>
      {/* Mobile / xs: only the current segment */}
      <p className="line-clamp-2 min-w-0 flex-1 break-words font-editorial text-[14px] font-medium leading-tight text-jq-navy sm:hidden">
        {last}
      </p>

      {/* Tablet+ : full path with chevrons */}
      <nav aria-label={t('shell.breadcrumbLabel')} className="hidden min-w-0 sm:block">
        <ol className="flex min-w-0 items-center gap-2 text-[12px] font-medium uppercase tracking-[0.18em]">
          {parents.map((segment) => (
            <li key={segment} className="flex shrink-0 items-center gap-2 text-jq-graphite">
              <span>{segment}</span>
              <ChevronRight className="h-3 w-3 text-jq-stone" strokeWidth={1.5} />
            </li>
          ))}
          <li className="truncate text-jq-navy">{last}</li>
        </ol>
      </nav>
    </>
  );
}

function SearchPill() {
  const { t } = useTranslation('app-shell');
  return (
    <>
      {/* Compact (mobile + tablet): icon button only */}
      <button
        type="button"
        aria-label={t('topbar.search')}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-jq-sand/70 bg-jq-surface-0 text-jq-graphite transition hover:border-jq-navy/30 hover:text-jq-navy lg:hidden"
      >
        <Search className="h-4 w-4" strokeWidth={1.5} />
      </button>

      {/* Desktop: full pill */}
      <button
        type="button"
        className="group hidden h-9 w-64 items-center gap-2 rounded-md border border-jq-sand/70 bg-jq-surface-0 px-3 text-left text-[13px] text-jq-graphite transition hover:border-jq-navy/30 hover:text-jq-navy lg:inline-flex xl:w-72"
      >
        <Search
          className="h-4 w-4 shrink-0 text-jq-stone group-hover:text-jq-navy"
          strokeWidth={1.5}
        />
        <span className="flex-1 truncate">{t('topbar.searchPlaceholder')}</span>
        <kbd className="hidden shrink-0 items-center gap-0.5 rounded border border-jq-sand/80 bg-jq-paper px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-jq-stone xl:inline-flex">
          <span className="text-[12px] leading-none">⌘</span>
          <span>K</span>
        </kbd>
      </button>
    </>
  );
}

function NotificationsButton({ hasUnread }: { hasUnread?: boolean }) {
  const { t } = useTranslation('app-shell');
  return (
    <button
      type="button"
      aria-label={hasUnread ? t('topbar.notificationsUnread') : t('topbar.notifications')}
      className="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-jq-sand/70 bg-jq-surface-0 text-jq-graphite transition hover:border-jq-navy/30 hover:text-jq-navy"
    >
      <Bell className="h-4 w-4" strokeWidth={1.5} />
      {hasUnread && (
        <span
          aria-hidden
          className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-jq-gold ring-2 ring-jq-paper"
        />
      )}
    </button>
  );
}

/** Exported for testing (account menu of the top bar). */
export function HeaderUserMenu() {
  const { t } = useTranslation('app-shell');
  const { session, signOut } = useAuthState();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const email = session?.user.email ?? '';
  // Raw `super_admin` is a backend enum value, not copy: translate it.
  const role = session?.user.role ? enumLabel(t, 'userRole', session.user.role) : '';
  const initials = computeInitials(email);

  async function onSignOut() {
    setOpen(false);
    await signOut();
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('topbar.accountMenu', { email })}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-jq-ink font-sans text-[12px] font-semibold uppercase tracking-wide text-jq-gold-bright ring-1 ring-jq-gold/40 transition hover:ring-jq-gold-bright"
      >
        {initials}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+8px)] z-40 w-64 overflow-hidden rounded-md border border-jq-navy-700 bg-jq-dark-2 text-jq-paper shadow-lg"
        >
          <div className="flex items-center gap-3 border-b border-jq-navy-700 px-4 py-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-jq-navy-700 font-sans text-[12px] font-semibold uppercase tracking-wide text-jq-gold-bright ring-1 ring-jq-gold/40">
              {initials}
            </span>
            <div className="min-w-0">
              <p className="truncate text-[13px] font-medium text-jq-paper">{email}</p>
              <p className="mt-0.5 truncate text-[10px] font-medium uppercase tracking-[0.22em] text-jq-gold-bright">
                {role}
              </p>
            </div>
          </div>
          <div className="p-1">
            <button
              type="button"
              role="menuitem"
              onClick={onSignOut}
              className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-[13px] font-medium text-jq-paper transition hover:bg-white/[0.05]"
            >
              <LogOut className="h-4 w-4 text-jq-navy-200" strokeWidth={1.5} />
              {t('shell.signOut')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function computeInitials(email: string): string {
  return (
    email
      .split('@')[0]
      ?.split(/[._-]/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'U'
  );
}
